import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DocMasterService } from 'src/app/Common/DocMasterService/docMaster.service';
import { DocMasterCreateRequestDTO, DocMasterDeleteRequestDTO, DocMasterDTO, DocMasterReadByMasterIdRequestDTO } from 'src/app/Common/DocMasterService/docMaster.interface';
import { DocumentMasterService } from 'src/app/Common/DocumentMasterService/document-master.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ConfirmmodalserviceService } from '../confirm-delete-modal/confirmmodalservice.service';

type PreviewType = 'image' | 'pdf' | 'office' | 'video' | 'other' | '';

@Component({
  selector: 'app-simple-document-upload',
  templateUrl: './simple-document-upload.component.html',
  styleUrls: ['./simple-document-upload.component.css']
})
export class SimpleDocumentUploadComponent implements OnInit, OnDestroy {
  @Input() masterId: number = 0;
  @Input() masterType: string = '';

  @ViewChild('videoRef') videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasRef') canvasRef?: ElementRef<HTMLCanvasElement>;

  User = inject(UserLoginService).User();
  private docMasterService = inject(DocMasterService);
  private documentMasterService = inject(DocumentMasterService);
  private sanitizer = inject(DomSanitizer);
  private toastr = inject(ToastrService);
  private confirmModal = inject(ConfirmmodalserviceService);

  documents: DocMasterDTO[] = [];
  loading = false;
  uploading = false;

  previewOpen = false;
  previewType: PreviewType = '';
  previewUrl: SafeResourceUrl | string = '';
  previewRawUrl = '';

  captureModalOpen = false;
  cameraStream: MediaStream | null = null;
  capturedPhotoPreview: string | null = null;
  private capturedPhotoBlob: Blob | null = null;

  ngOnInit(): void {
    this.loadDocuments();
  }

  ngOnDestroy(): void {
    this.stopCameraStream();
  }

  loadDocuments(): void {
    if (!this.masterId || !this.masterType) return;

    this.loading = true;
    const data: DocMasterReadByMasterIdRequestDTO = {
      masterId: this.masterId,
      masterType: this.masterType,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.docMasterService.DocMaster_ReadByMasterId(data).subscribe({
      next: (res) => {
        this.documents = res.items || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onBrowseSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(input.files);
    input.value = '';
  }

  async openCapture(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.toastr.error('No webcam detected on this device', 'Camera Unavailable');
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(d => d.kind === 'videoinput');
      if (!hasCamera) {
        this.toastr.error('No webcam detected on this device', 'Camera Unavailable');
        return;
      }

      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      this.capturedPhotoBlob = null;
      this.capturedPhotoPreview = null;
      this.captureModalOpen = true;
      this.attachStreamToVideo();
    } catch (err: any) {
      if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        this.toastr.error('No webcam detected on this device', 'Camera Unavailable');
      } else if (err?.name === 'NotAllowedError') {
        this.toastr.error('Camera permission was denied', 'Camera Unavailable');
      } else {
        this.toastr.error('Unable to access webcam', 'Camera Error');
      }
    }
  }

  private attachStreamToVideo(): void {
    setTimeout(() => {
      if (this.videoRef?.nativeElement && this.cameraStream) {
        this.videoRef.nativeElement.srcObject = this.cameraStream;
      }
    });
  }

  takePhoto(): void {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(blob => {
      if (!blob) return;
      this.capturedPhotoBlob = blob;
      this.capturedPhotoPreview = canvas.toDataURL('image/jpeg');
    }, 'image/jpeg', 0.9);
  }

  retakePhoto(): void {
    this.capturedPhotoBlob = null;
    this.capturedPhotoPreview = null;
    this.attachStreamToVideo();
  }

  useCapturedPhoto(): void {
    if (!this.capturedPhotoBlob) return;
    const file = new File([this.capturedPhotoBlob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    this.closeCaptureModal();
    this.handleFiles([file]);
  }

  closeCaptureModal(): void {
    this.stopCameraStream();
    this.captureModalOpen = false;
    this.capturedPhotoBlob = null;
    this.capturedPhotoPreview = null;
  }

  private stopCameraStream(): void {
    this.cameraStream?.getTracks().forEach(t => t.stop());
    this.cameraStream = null;
  }

  private handleFiles(files: FileList | File[] | null): void {
    if (!files || !files.length) return;

    this.uploading = true;
    const uploads = Array.from(files).map(file => this.uploadAndSave(file));

    forkJoin(uploads).subscribe({
      next: () => {
        this.uploading = false;
        this.loadDocuments();
        this.toastr.success('Document(s) uploaded successfully', 'Success');
      },
      error: () => {
        this.uploading = false;
        this.toastr.error('Upload failed', 'Error');
      }
    });
  }

  private uploadAndSave(file: File): Observable<DocMasterDTO> {
    const fd = new FormData();
    fd.append('file', file, file.name);

    return this.documentMasterService.DocumentMaster_Upload(fd).pipe(
      switchMap((resp: any) => {
        const url = resp?.url ?? resp?.path ?? resp?.file ?? '';
        const payload: DocMasterCreateRequestDTO = {
          tenantId: this.User.companyId,
          tenantBranchId: this.User.loggedInBranchId,
          masterId: this.masterId,
          masterType: this.masterType,
          docName: file.name,
          docDesc: '',
          docType: this.getDocTypeFromFile(file),
          docURL: url,
          actionUser: this.User.userId.toString()
        };
        return this.docMasterService.DocMaster_Create(payload);
      })
    );
  }

  private getDocTypeFromFile(file: File): string {
    if (file.type.startsWith('image/')) return 'Image';
    if (file.type.startsWith('video/')) return 'Video';
    if (file.type === 'application/pdf') return 'PDF';
    return 'Other';
  }

  downloadDocument(doc: DocMasterDTO, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!doc.docURL) return;
    this.downloadFromUrl(doc.docURL, doc.docName || 'document');
  }

  downloadPreviewFile(): void {
    if (!this.previewRawUrl) return;
    this.downloadFromUrl(this.previewRawUrl, this.getFileNameFromUrl(this.previewRawUrl));
  }

  private downloadFromUrl(url: string, fileName: string): void {
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        this.toastr.error('Unable to download file', 'Error');
      });
  }

  private getFileNameFromUrl(url: string): string {
    const clean = url.split('?')[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1)) || 'document';
  }

  deleteDocument(doc: DocMasterDTO, event?: Event): void {
    event?.stopPropagation();

    this.confirmModal.openDeleteModal(doc.docName || 'this document', doc).subscribe(res => {
      if (!res) return;

      const data: DocMasterDeleteRequestDTO = {
        docId: doc.docId,
        tenantId: doc.tenantId ?? this.User.companyId,
        tenantBranchId: doc.tenantBranchId ?? this.User.loggedInBranchId,
        actionUser: this.User.userId.toString()
      };

      this.docMasterService.DocMaster_Delete(data).subscribe(() => {
        this.loadDocuments();
        this.toastr.success('Document deleted successfully', 'Success');
      });
    });
  }

  viewDocument(doc: DocMasterDTO): void {
    const url = doc.docURL || '';
    if (!url) return;

    this.previewRawUrl = url;

    if (/\.(jpe?g|png|gif|webp|svg|bmp|ico)$/i.test(url)) {
      this.previewType = 'image';
      this.previewUrl = url;
    } else if (/\.pdf$/i.test(url)) {
      this.previewType = 'pdf';
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else if (/\.(docx?|xlsx?|pptx?)$/i.test(url)) {
      this.previewType = 'office';
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        'https://docs.google.com/gview?url=' + encodeURIComponent(url) + '&embedded=true'
      );
    } else if (/\.(mp4|webm|mov|avi|mkv)$/i.test(url)) {
      this.previewType = 'video';
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.previewType = 'other';
      this.previewUrl = url;
    }

    this.previewOpen = true;
  }

  closePreview(): void {
    this.previewOpen = false;
    this.previewType = '';
    this.previewUrl = '';
    this.previewRawUrl = '';
  }

  getFileIcon(doc: DocMasterDTO): string {
    const url = (doc.docURL || '').toLowerCase();
    if (/\.(jpe?g|png|gif|webp|svg|bmp|ico)$/.test(url)) return 'bi bi-file-earmark-image-fill';
    if (/\.pdf$/.test(url)) return 'bi bi-file-earmark-pdf-fill';
    if (/\.(docx?)$/.test(url)) return 'bi bi-file-earmark-word-fill';
    if (/\.(xlsx?)$/.test(url)) return 'bi bi-file-earmark-excel-fill';
    if (/\.(pptx?)$/.test(url)) return 'bi bi-file-earmark-ppt-fill';
    if (/\.(mp4|webm|mov|avi|mkv)$/.test(url)) return 'bi bi-file-earmark-play-fill';
    return 'bi bi-file-earmark-fill';
  }

  getFileIconClass(doc: DocMasterDTO): string {
    const url = (doc.docURL || '').toLowerCase();
    if (/\.(jpe?g|png|gif|webp|svg|bmp|ico)$/.test(url)) return 'sdu-icon--image';
    if (/\.pdf$/.test(url)) return 'sdu-icon--pdf';
    if (/\.(docx?)$/.test(url)) return 'sdu-icon--word';
    if (/\.(xlsx?)$/.test(url)) return 'sdu-icon--excel';
    if (/\.(pptx?)$/.test(url)) return 'sdu-icon--ppt';
    if (/\.(mp4|webm|mov|avi|mkv)$/.test(url)) return 'sdu-icon--video';
    return 'sdu-icon--other';
  }

  getFileExt(doc: DocMasterDTO): string {
    const url = (doc.docURL || '').split('?')[0];
    return (url.split('.').pop() || 'FILE').toUpperCase().slice(0, 6);
  }

  formatDate(d?: Date | string): string {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  }
}
